import React from 'react'
import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_self_service_5'

const URLS = {
  MX_RECORDS_SETUP: 'https://campus.barracuda.com/doc/96022975/'
}

function SelfService5I18n({ classes, productName }: { classes: ReturnType<typeof styles>; productName?: string }) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatValues = { productName }

  return (
    <div className={classes.content} data-testid="content">
      {formatMessage('intro')}
      <ol>
        <li>
          {formatMessage('mx_records.step_1_prefix', formatValues)}
          <a href={URLS.MX_RECORDS_SETUP} target="_new">
            {formatMessage('mx_records.step_1_link')}
          </a>
          {formatMessage('mx_records.step_1_suffix')}
        </li>
        <li>{formatMessage('mx_records.step_2')}</li>
      </ol>
      <p>{formatMessage('alternate_validation_intro')}</p>
      <ul>
        <li>
          <b>{formatMessage('validation_methods.cname.label')}</b> -{' '}
          {formatMessage('validation_methods.cname.description')}
        </li>
        <ol>
          <li>{formatMessage('validation_methods.cname.steps.step_1')}</li>
          <li>{formatMessage('validation_methods.cname.steps.step_2')}</li>
          <li>{formatMessage('validation_methods.cname.steps.step_3')}</li>
        </ol>
        <li>
          <b>{formatMessage('validation_methods.email_postmaster.label')}</b> -{' '}
          {formatMessage('validation_methods.email_postmaster.description')}
        </li>
        <li>
          <b>{formatMessage('validation_methods.email_technical.label')}</b> -{' '}
          {formatMessage('validation_methods.email_technical.description', formatValues)}
        </li>
      </ul>
    </div>
  )
}

function SelfService5(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <SelfService5I18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      To verify your domain, configure your MX records:
      <ol>
        <li>
          Replace your current MX records with {props.productName} MX records displayed on the page. If you want to
          first test {props.productName}, or you just want to be careful moving your mail to the Barracuda Networks
          service, then just ADD the MX records with a LOW priority (99 for example). This will allow you to complete
          the verification process, but your legitimate mail will still use your current mail server. See{' '}
          <a href="https://campus.barracuda.com/doc/96022975/" target="_new">
            How to Set Up MX Records for Domain Verification
          </a>{' '}
          in Barracuda Campus for an example.
        </li>
        <li>
          After you have changed your MX records, click the <b>Verify MX Records</b> button.
        </li>
      </ol>
      <p>
        If you do not want to route your email through the service at this time, but want to verify your domain
        ownership, check the box, and you&apos;ll see these other options for domain validation:
      </p>
      <ul>
        <li>
          <b>CNAME</b> - Follow these steps to use CNAME validation:
        </li>
        <ol>
          <li>
            Log into your DNS Server and, under this domain, create a subdomain whose name is created by concatenating
            barracuda and the CNAME token as shown on the page.
          </li>
          <li>
            Point the CNAME record of that subdomain to <code>ess.barracuda.com</code>
            <br />
            You might have to wait for a short time for the DNS propagation to take effect.
          </li>
          <li>
            Click <b>Confirm Validation</b> on the page to complete the domain verification process. This domain should
            then show as <i>validated</i> in the DOMAINS page when you log into the service web interface.
          </li>
        </ol>
        <li>
          <b>Email to Postmaster</b> - This method sends a verification email to the postmaster email address for your
          domain. The confirmation email will include a link that the recipient can click to verify the domain.
        </li>
        <li>
          <b>Email to Technical Contact</b> - This method sends a verification email to the Technical Contact email
          address, if it exists, listed on your domain&apos;s WHOIS entry. This verification option is not available if{' '}
          {props.productName} cannot find your domain&apos;s WHOIS entry. If there is not a Technical Contact, then only
          the <b>MX Records</b>, <b>CNAME</b> and <b>Email to the Postmaster</b> options are available.
        </li>
      </ul>
    </div>
  )
}

export default SelfService5
